import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Loader2, Save, X, Building2, Phone, Globe, MapPin, FileText, Users, Mail } from 'lucide-react';
import { PhoneInput } from '@/components/ui/PhoneInput';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EntrepriseProfileEdit = ({ profile, user, token, onSaved, onCancel }) => {
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    company_name: profile?.company_name || '',
    sector: profile?.sector || '',
    size: profile?.size || '',
    description: profile?.description || '',
    website: profile?.website || '',
    siret: profile?.siret || '',
    contact_name: profile?.contact_name || '',
    contact_email: profile?.contact_email || '',
    contact_phone: profile?.contact_phone || '',
    address: profile?.address || '',
    city: profile?.city || '',
    postal_code: profile?.postal_code || '',
    country: profile?.country || 'France',
  });

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const formData = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        if (value !== '' && value !== null && value !== undefined) {
          formData.append(key, value);
        }
      });

      const res = await fetch(`${API_URL}/api/entreprises/me`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      if (res.ok) {
        const data = await res.json();
        toast.success('Profil mis à jour avec succès');
        onSaved(data.profile);
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur lors de la mise à jour');
      }
    } catch (err) {
      toast.error('Erreur de connexion');
    } finally {
      setSaving(false);
    }
  };

  const Field = ({ label, icon: Icon, children }) => (
    <div>
      <label className="flex items-center gap-1.5 text-sm font-medium text-foreground mb-1.5">
        {Icon && <Icon size={14} className="text-muted-foreground" />}
        {label}
      </label>
      {children}
    </div>
  );

  const inputCls = "w-full h-10 px-3 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary transition-colors";
  const textareaCls = "w-full px-3 py-2 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary transition-colors min-h-[80px] resize-y";

  const sizes = [
    { value: '1-9', label: '1-9 employés' },
    { value: '10-49', label: '10-49 employés' },
    { value: '50-249', label: '50-249 employés' },
    { value: '250-999', label: '250-999 employés' },
    { value: '1000+', label: '1000+ employés' },
  ];

  return (
    <form onSubmit={handleSubmit} className="space-y-6" data-testid="entreprise-profile-edit-form">
      {/* Company Info */}
      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
          <Building2 size={18} /> Informations entreprise
        </h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Nom de l'entreprise" icon={Building2}>
            <input type="text" value={form.company_name} onChange={e => handleChange('company_name', e.target.value)}
              className={inputCls} data-testid="edit-company-name" />
          </Field>
          <Field label="Secteur" icon={FileText}>
            <input type="text" value={form.sector} onChange={e => handleChange('sector', e.target.value)}
              placeholder="Ex: Tech & Innovation" className={inputCls} data-testid="edit-sector" />
          </Field>
          <Field label="Taille" icon={Users}>
            <select value={form.size} onChange={e => handleChange('size', e.target.value)}
              className={inputCls} data-testid="edit-size">
              <option value="">Sélectionner</option>
              {sizes.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
            </select>
          </Field>
          <Field label="SIRET">
            <input type="text" value={form.siret} onChange={e => handleChange('siret', e.target.value)}
              placeholder="Ex: 123 456 789 00012" className={inputCls} data-testid="edit-siret" />
          </Field>
          <Field label="Site web" icon={Globe}>
            <input type="url" value={form.website} onChange={e => handleChange('website', e.target.value)}
              placeholder="https://..." className={inputCls} data-testid="edit-website" />
          </Field>
        </div>
        <div className="mt-4">
          <Field label="Description" icon={FileText}>
            <textarea value={form.description} onChange={e => handleChange('description', e.target.value)}
              placeholder="Décrivez votre entreprise et vos besoins..." className={textareaCls} data-testid="edit-description" />
          </Field>
        </div>
      </div>

      {/* Contact */}
      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
          <Phone size={18} /> Contact
        </h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Nom du contact" icon={Users}>
            <input type="text" value={form.contact_name} onChange={e => handleChange('contact_name', e.target.value)}
              className={inputCls} data-testid="edit-contact-name" />
          </Field>
          <Field label="Email du contact" icon={Mail}>
            <input type="email" value={form.contact_email} onChange={e => handleChange('contact_email', e.target.value)}
              className={inputCls} data-testid="edit-contact-email" />
          </Field>
          <Field label="Téléphone" icon={Phone}>
            <PhoneInput value={form.contact_phone} onChange={val => handleChange('contact_phone', val || '')}
              data-testid="edit-contact-phone" />
          </Field>
        </div>
      </div>

      {/* Address */}
      <div className="bg-card border border-border rounded-xl p-6">
        <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
          <MapPin size={18} /> Adresse
        </h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">
            <Field label="Adresse" icon={MapPin}>
              <input type="text" value={form.address} onChange={e => handleChange('address', e.target.value)}
                placeholder="123 rue de la Paix" className={inputCls} data-testid="edit-address" />
            </Field>
          </div>
          <Field label="Ville">
            <input type="text" value={form.city} onChange={e => handleChange('city', e.target.value)}
              placeholder="Paris" className={inputCls} data-testid="edit-city" />
          </Field>
          <Field label="Code postal">
            <input type="text" value={form.postal_code} onChange={e => handleChange('postal_code', e.target.value)}
              placeholder="75001" className={inputCls} data-testid="edit-postal-code" />
          </Field>
          <Field label="Pays">
            <input type="text" value={form.country} onChange={e => handleChange('country', e.target.value)}
              className={inputCls} data-testid="edit-country" />
          </Field>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-3 justify-end">
        <Button type="button" variant="outline" onClick={onCancel} data-testid="edit-cancel-btn">
          <X size={16} className="mr-1" /> Annuler
        </Button>
        <Button type="submit" disabled={saving} data-testid="edit-save-btn">
          {saving ? <><Loader2 size={16} className="mr-1 animate-spin" /> Enregistrement...</> :
            <><Save size={16} className="mr-1" /> Enregistrer</>}
        </Button>
      </div>
    </form>
  );
};

export default EntrepriseProfileEdit;
